<template>
	<div class="meeting-filter">
		<portal-filter-bar :label="$t('meetings.filter.heading')">
			<portal-search-field
				:value="filter.search || ''"
				:label="$t('meetings.filter.heading')"
				:placeholder="$t('global.filter')"
				:clear-label="$t('global.clear')"
				@input="updateSearch"
			></portal-search-field>
			<portal-select-field
				field-class="status-filter"
				:value="meetingStatusValue"
				:label="$t('meetings.filter.meeting-status')"
				:options="meetingStatusOptions"
				@input="updateMeetingStatus"
			></portal-select-field>
			<portal-select-field
				field-class="date-filter"
				:value="filter.dateSpecifier || ''"
				:label="$t('meetings.filter.date-range')"
				:options="dateOptions"
				@input="updateDateSpecifier"
			></portal-select-field>
		</portal-filter-bar>

		<div class="custom-date-range" v-if="filter.dateSpecifier === 'between'">
			<div class="custom-date-field">
				<label>{{ $t('meetings.filter.earliest') }}</label>
				<date-selector
					:date="filter.earliest"
					:label="$t('meetings.filter.earliest')"
					:panel="true"
					@change="updateDate('earliest', $event)"
				></date-selector>
			</div>
			<span class="date-divider" aria-hidden="true"></span>
			<div class="custom-date-field">
				<label>{{ $t('meetings.filter.latest') }}</label>
				<date-selector
					:date="filter.latest"
					:label="$t('meetings.filter.latest')"
					:panel="true"
					@change="updateDate('latest', $event)"
				></date-selector>
			</div>
			<button type="button" class="clear-filter" @click="clearFilter">
				<portal-icon name="x" :size="15"></portal-icon>
				{{ $t('meetings.filter.clear-filter') }}
			</button>
		</div>
	</div>
</template>

<script>
	import { DateTime } from 'luxon';
	import FilterHelpers from '@/utils/FilterHelpers';
	import DateSelector from '@/components/DateSelector';
	import PortalFilterBar from '@/components/filters/PortalFilterBar';
	import PortalSearchField from '@/components/filters/PortalSearchField';
	import PortalSelectField from '@/components/filters/PortalSelectField';

	export default {
		name: 'MeetingListFilter',

		props: ['value'],

		components: {
			DateSelector,
			PortalFilterBar,
			PortalSearchField,
			PortalSelectField,
		},

		data() {
			return {
				filter: JSON.parse(JSON.stringify(this.value)),
				dateOptions: FilterHelpers.getDateOptions([
					'all-time',
					'today',
					'next-30-days',
					'next-60-days',
					'next-90-days',
					'past-30-days',
					'past-60-days',
					'past-90-days',
					'past-6-months',
					'past-12-months',
					'this-year',
					'last-year',
					'between',
				]),
			};
		},

		computed: {
			meetingStatusValue() {
				return this.filter.meetingStatus && this.filter.meetingStatus.length
					? this.filter.meetingStatus[0]
					: '';
			},

			meetingStatusOptions() {
				return [
					{ text: this.$t('projects.filter.all'), value: '' },
					{ text: this.$t('meetings.filter.scheduled'), value: 'Scheduled' },
					{ text: this.$t('meetings.filter.cancelled'), value: 'Cancelled' },
					{ text: this.$t('meetings.filter.no-show'), value: 'NoShow' },
				];
			},
		},

		methods: {
			emitUpdated() {
				this.$emit('input', JSON.parse(JSON.stringify(this.filter)));
			},

			updateSearch(value) {
				this.filter.search = value || null;
				this.emitUpdated();
			},

			updateMeetingStatus(value) {
				this.filter.meetingStatus = value ? [value] : [];
				this.emitUpdated();
			},

			updateDateSpecifier(value) {
				this.filter.dateSpecifier = value || null;
				if (value === 'between') {
					if (!this.filter.earliest) {
						this.filter.earliest = DateTime.local().toISODate();
					}
					if (!this.filter.latest) {
						this.filter.latest = DateTime.local().plus({ days: 30 }).toISODate();
					}
				}
				this.emitUpdated();
			},

			updateDate(field, value) {
				this.filter[field] = value;
				this.emitUpdated();
			},

			clearFilter() {
				this.$emit('clear-filter');
			},
		},

		watch: {
			value: {
				deep: true,
				handler(value) {
					this.filter = JSON.parse(JSON.stringify(value));
				},
			},
		},
	};
</script>

<style scoped lang="scss">
	.meeting-filter {
		display: grid;
		gap: 10px;
	}

	.custom-date-range {
		display: flex;
		align-items: flex-end;
		gap: 10px;
		padding: 12px;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
	}

	.custom-date-field {
		min-width: 150px;
		flex: 0 1 190px;
	}

	.custom-date-field > label {
		display: block;
		margin: 0 0 6px 2px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 600;
	}

	.date-divider {
		width: 12px;
		height: 1px;
		margin-bottom: 19px;
		background: var(--portal-muted);
	}

	.clear-filter {
		min-height: 38px;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		margin-left: auto;
		padding: 0 10px;
		border: 0;
		border-radius: var(--brand-button-radius, 10px);
		background: transparent;
		color: var(--portal-muted);
		font: inherit;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
	}

	.clear-filter:hover,
	.clear-filter:focus-visible {
		outline: 0;
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	@media (min-width: 760px) {
		.meeting-filter ::v-deep .portal-search-field {
			width: 220px;
			min-width: 180px;
			max-width: 220px;
			flex: 0 1 220px;
		}

		.meeting-filter ::v-deep .portal-select-field.status-filter {
			min-width: 205px;
			flex: 0 0 205px;
		}

		.meeting-filter ::v-deep .portal-select-field.date-filter {
			min-width: 230px;
			flex: 1 1 230px;
		}
	}

	@media (max-width: 599px) {
		.custom-date-range {
			display: grid;
			grid-template-columns: minmax(0, 1fr);
		}

		.custom-date-field {
			width: 100%;
		}

		.date-divider {
			display: none;
		}

		.clear-filter {
			margin-left: 0;
			justify-self: start;
		}
	}
</style>
